import { EAssetType, EProgressBarType } from 'constants/enums';
import { PROGRESS_BAR_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';

import { InputSelectDropdownOption } from 'components/InputSelectDropdown/InputSelectDropdown';

import {
  PERCENTAGE_LEFT_ICON,
  PERCENTAGE_LEFT_ICON_STYLES
} from '../FormFields.consts';
import {
  GradientFieldConfig,
  NumberFieldConfig,
  UploadFieldConfig
} from '../FormFields.types';

export const PROGRESS_BAR_TYPE_OPTIONS: InputSelectDropdownOption[] = [
  { value: EProgressBarType.CLASSIC, label: 'Classic' },
  { value: EProgressBarType.MILESTONE, label: 'Milestone' }
];

export const PROGRESS_BAR_TYPE_TOOLTIP =
  'How missions are displayed on the bar. Classic: one mission at a time. Milestone: all missions visible at once.';

export const FONT_WEIGHT_OPTIONS = [
  { value: 'normal', label: 'Regular' },
  { value: 'bold', label: 'Bold' },
  { value: 'bolder', label: 'Bolder' }
];

export const TITLE_DEFAULTS = {
  fontSize: PROGRESS_BAR_DEFAULTS.titleFontSize
};

export const SUBTITLE_DEFAULTS = {
  fontSize: PROGRESS_BAR_DEFAULTS.subTitleFontSize
};

export const FRAME_NUMBER_FIELDS: NumberFieldConfig[] = [
  {
    label: 'Background Opacity',
    name: 'backgroundOpacity',
    min: 0,
    max: 100,
    isRequired: true,
    leftIcon: PERCENTAGE_LEFT_ICON,
    leftIconStyles: PERCENTAGE_LEFT_ICON_STYLES
  },
  { label: 'Frame Radius', name: 'frameRadius', min: 0, isRequired: true }
];

export const BAR_FILL_COLOR_FIELD: GradientFieldConfig = {
  header: 'Bar Fill Color',
  name: 'barFillColor',
  fallback: PROGRESS_BAR_DEFAULTS.barFillColor,
  dataTestId: 'offer-design-progress-bar-fill-color-input'
};

export const BORDER_COLOR_FIELD: GradientFieldConfig = {
  header: 'Border Color',
  name: 'borderColor',
  fallback: PROGRESS_BAR_DEFAULTS.borderColor,
  dataTestId: 'offer-design-progress-bar-border-color-input',
  tooltip:
    'Applies to all borders, including the bar, tooltip, and points background.'
};

export const BACKGROUND_COLOR_FIELD: GradientFieldConfig = {
  header: 'Background Color',
  name: 'barBackgroundColor',
  fallback: PROGRESS_BAR_DEFAULTS.backgroundColor,
  dataTestId: 'offer-design-progress-bar-background-color-input'
};

export const BORDER_BACKGROUND_GRADIENT_FIELDS: GradientFieldConfig[] = [
  BORDER_COLOR_FIELD,
  BACKGROUND_COLOR_FIELD
];

export const BAR_NUMBER_FIELDS: NumberFieldConfig[] = [
  { label: 'Bar Radius', name: 'barRadius', min: 0 },
  { label: 'Border Width', name: 'borderWidth', min: 0 }
];

const SHARED_OPTIONAL_UPLOAD_FIELDS: UploadFieldConfig[] = [
  {
    title: 'Background Image',
    fieldName: 'barBackgroundImage',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload a background image for the progress bar'
  },
  {
    title: 'Header Image',
    fieldName: 'headerImage',
    forceType: EAssetType.OFFER_HEADER,
    tooltip: 'Upload a header image for the progress bar'
  }
];

export const CLASSIC_UPLOAD_FIELDS: UploadFieldConfig[] = [
  {
    title: 'Accumulated Item Image',
    fieldName: 'accumulatedItemImageUrl',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload a points ribbon icon for the progress bar',
    isRequired: true
  },
  {
    title: 'Accumulated Item Ribbon Image',
    fieldName: 'accumulatedItemBackgroundImageUrl',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload a points ribbon background for the progress bar',
    isRequired: true
  },
  ...SHARED_OPTIONAL_UPLOAD_FIELDS
];

export const MILESTONE_UPLOAD_FIELDS: UploadFieldConfig[] = [
  {
    title: 'Accumulated Item Image',
    fieldName: 'accumulatedItemImageUrl',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload an accumulated item image for the progress bar',
    isRequired: true
  },
  ...SHARED_OPTIONAL_UPLOAD_FIELDS,
  {
    title: 'Accumulated Item Ribbon Image',
    fieldName: 'accumulatedItemBackgroundImageUrl',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload a points badge image shown on qualifying offers',
    isRequired: true
  },
  {
    title: 'Milestone Completed Icon Image',
    fieldName: 'collectedIconImage',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload an icon shown when a milestone reward is collected',
    isRequired: true
  },
  {
    title: 'Tooltip Background',
    fieldName: 'tooltipBackgroundImage',
    forceType: EAssetType.BG_BUNDLE,
    tooltip: 'Upload a background image for milestone tooltips'
  }
];

export const findProgressBarTypeOption = (
  currentValue: EProgressBarType | undefined
): InputSelectDropdownOption => {
  const resolvedValue = currentValue || EProgressBarType.CLASSIC;
  return (
    PROGRESS_BAR_TYPE_OPTIONS.find(
      (option) => option.value === resolvedValue
    ) || PROGRESS_BAR_TYPE_OPTIONS[0]
  );
};
